import { Divider } from "@mui/material";

import { formatMoneyString } from "src/utility/utilityFunctions";

import { OrderVariant } from "@models";
import { cn } from "@utils/tailwind";

import PaperTooltip from "../../ui/PaperTooltip";
import CompareCosts from "../CompareCosts";
import { getCoopExtendedCost, getExtendedCost } from "../helpers";
import { useCurrentOrderSet } from "./data/CurrentOrderSetContext";

export default function OrderVariantCostBreakdown({
  orderVariant,
}: {
  orderVariant: OrderVariant;
}) {
  const { isCoopOrderSet } = useCurrentOrderSet();
  const extendedCost = getExtendedCost(orderVariant);
  const costIncludingCoopDiscount = isCoopOrderSet
    ? getCoopExtendedCost(orderVariant, orderVariant.orderOrderingType)
    : extendedCost;

  const coopDiscount = extendedCost - costIncludingCoopDiscount;

  return (
    <PaperTooltip
      placement="right-end"
      title={
        <>
          <h3 className="font-medium mb-3">Cost break-down</h3>
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <div className="text-neutral-500 tracking-wide">Item</div>
            <div
              className={cn(
                "text-right",
                +orderVariant.billableTotalEstimatedCost === 0 &&
                  "line-through opacity-50"
              )}
            >
              {orderVariant.qty} x{" "}
              {formatMoneyString(orderVariant.estimatedCost)} ={" "}
              {formatMoneyString(orderVariant.totalEstimatedCost)}
            </div>

            {orderVariant.variant.item.includeBeacon && (
              <>
                <div className="text-neutral-500 tracking-wide">Beacons</div>
                <div className="text-right">
                  <CompareCosts
                    cost={orderVariant.billableTotalBeaconCost}
                    comparedTo={orderVariant.totalBeaconCost}
                  />
                </div>
              </>
            )}

            <div className="text-neutral-500 tracking-wide">Freight</div>
            <div className="text-right">
              <CompareCosts
                cost={orderVariant.billableTotalEstimatedShippingCost}
                comparedTo={orderVariant.totalEstimatedShippingCost}
              />
            </div>

            <div className="text-neutral-500 tracking-wide">Taxes</div>
            <div className="text-right">
              <CompareCosts
                cost={orderVariant.billableTotalEstimatedTax}
                comparedTo={orderVariant.totalEstimatedTax}
              />
            </div>
          </div>
          {coopDiscount > 0 && (
            <div className="flex gap-4 justify-between px-2 py-1 -mx-2 bg-primary-50 text-primary-600">
              <div className="tracking-wide">Co-Op Coverage</div>
              <div className="text-right">
                {formatMoneyString(-coopDiscount)}
              </div>
            </div>
          )}
          <Divider className="col-span-2 my-1" />
          <div className="flex justify-between">
            <div className="text-neutral-500 tracking-wide">Total</div>
            <div className="font-medium text-right">
              {formatMoneyString(costIncludingCoopDiscount)}
            </div>
          </div>
        </>
      }
    >
      <div className="hover:bg-neutral-100 rounded">
        ={formatMoneyString(costIncludingCoopDiscount)}
      </div>
    </PaperTooltip>
  );
}
